// Considered: ChannelSettingsTabOrganism — rejected because it is the legacy 4-tab settings panel without locale management or per-locale i18n fields. See spec Reuse Audit.
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { listPublishers } from '../../../api/marketplaceApi';
import type { Channel, ChannelScope } from 'types/channel';
import type { PublisherDetail } from 'types/publisher';
import LocaleManagerMolecule from '../../molecules/marketplace/LocaleManagerMolecule';
import LocaleTabsMolecule from '../../molecules/marketplace/LocaleTabsMolecule';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import './ChannelProfileSectionOrganism.scss';

export interface ChannelProfileDraft {
  locales: string[];
  defaultLocale: string;
  name_i18n: Record<string, string>;
  tagline_i18n: Record<string, string>;
  description_i18n: Record<string, string>;
  tags: string[];
  scope: ChannelScope;
  publisherSystemName: string | null;
}

export interface ChannelProfileSectionOrganismProps {
  channel: Channel;
  draft: ChannelProfileDraft;
  update: (patch: Partial<ChannelProfileDraft>) => void;
}

const NAME_MAX = 60;
const TAGLINE_MAX = 100;
const DESCRIPTION_MAX = 500;
const TAGS_MAX = 6;

const ChannelProfileSectionOrganism: React.FC<ChannelProfileSectionOrganismProps> = ({
  channel,
  draft,
  update,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [activeLocale, setActiveLocale] = useState<string>(draft.defaultLocale);
  const [tagInput, setTagInput] = useState('');
  const [publishers, setPublishers] = useState<PublisherDetail[]>([]);

  // Sync activeLocale if the defaultLocale changes (e.g., a new locale becomes default)
  useEffect(() => {
    if (!draft.locales.includes(activeLocale)) {
      setActiveLocale(draft.defaultLocale);
    }
  }, [draft.locales, draft.defaultLocale, activeLocale]);

  // Fetch publishers when scope is metasphere and we have API access
  useEffect(() => {
    if (draft.scope !== 'metasphere' || !apiClients) return;
    let cancelled = false;
    listPublishers(apiClients.schemaClient, { mine: true, status: 'published' })
      .then((res) => {
        if (!cancelled) setPublishers(res.publishers);
      })
      .catch(() => {
        // Non-fatal: publisher picker shows empty list
      });
    return () => { cancelled = true; };
  }, [draft.scope, apiClients]);

  const isDefault = activeLocale === draft.defaultLocale;

  const updateLocaleField = useCallback(
    (field: 'name_i18n' | 'tagline_i18n' | 'description_i18n', locale: string, value: string) => {
      update({ [field]: { ...draft[field], [locale]: value } });
    },
    [draft, update],
  );

  const handleAddLocale = useCallback(
    (code: string) => {
      if (draft.locales.includes(code)) return;
      update({ locales: [...draft.locales, code] });
      setActiveLocale(code);
    },
    [draft.locales, update],
  );

  const handleRemoveLocale = useCallback(
    (code: string) => {
      if (code === draft.defaultLocale) return;
      const next = draft.locales.filter((l) => l !== code);
      const nextName = { ...draft.name_i18n };
      const nextTagline = { ...draft.tagline_i18n };
      const nextDesc = { ...draft.description_i18n };
      delete nextName[code];
      delete nextTagline[code];
      delete nextDesc[code];
      update({ locales: next, name_i18n: nextName, tagline_i18n: nextTagline, description_i18n: nextDesc });
      if (activeLocale === code) setActiveLocale(draft.defaultLocale);
    },
    [draft, update, activeLocale],
  );

  const handleSetDefaultLocale = useCallback(
    (code: string) => {
      update({ defaultLocale: code });
    },
    [update],
  );

  const handleTagAdd = useCallback(() => {
    const normalized = tagInput.trim().toLowerCase();
    if (!normalized || draft.tags.includes(normalized) || draft.tags.length >= TAGS_MAX) return;
    update({ tags: [...draft.tags, normalized] });
    setTagInput('');
  }, [tagInput, draft.tags, update]);

  const handleTagRemove = useCallback(
    (tag: string) => {
      update({ tags: draft.tags.filter((t) => t !== tag) });
    },
    [draft.tags, update],
  );

  const currentName = draft.name_i18n[activeLocale] ?? '';
  const currentTagline = draft.tagline_i18n[activeLocale] ?? '';
  const currentDescription = draft.description_i18n[activeLocale] ?? '';
  const defaultName = draft.name_i18n[draft.defaultLocale] ?? '';
  const defaultTagline = draft.tagline_i18n[draft.defaultLocale] ?? '';
  const defaultDescription = draft.description_i18n[draft.defaultLocale] ?? '';

  const publisherOptions = [
    { label: t('marketplace.channelManage.v3Profile.noPublisher'), value: null as string | null },
    ...publishers.map((p) => ({ label: p.display_name, value: p.system_name })),
  ];

  return (
    <div className="channel-profile-section">
      {/* ── Public profile card ─────────────────────────────── */}
      <div className="channel-profile-section__card">
        <div className="channel-profile-section__card-header">
          <div className="channel-profile-section__card-title-row">
            <div>
              <h2 className="channel-profile-section__card-title">
                {t('marketplace.channelManage.v3Profile.publicProfileTitle')}
              </h2>
              <p className="channel-profile-section__card-subtitle">
                {t('marketplace.channelManage.v3Profile.publicProfileSubtitle')}
              </p>
            </div>
            <LocaleManagerMolecule
              locales={draft.locales}
              defaultLocale={draft.defaultLocale}
              onLocalesChange={(locales) => {
                // Handles add (manager adds from its list) — syncs locale list
                const added = locales.find((l) => !draft.locales.includes(l));
                if (added) handleAddLocale(added);
                else update({ locales });
              }}
              onDefaultLocaleChange={handleSetDefaultLocale}
            />
          </div>
          <LocaleTabsMolecule
            locales={draft.locales}
            defaultLocale={draft.defaultLocale}
            activeLocale={activeLocale}
            onLocaleChange={setActiveLocale}
            onRemove={handleRemoveLocale}
          />
        </div>

        {/* Fallback notice for non-default locales */}
        {!isDefault && (
          <div className="channel-profile-section__fallback-notice" data-testid="fallback-notice">
            <i className="pi pi-language channel-profile-section__fallback-icon" aria-hidden="true" />
            <span>
              {t('marketplace.channelManage.v3Profile.fallbackNotice', {
                locale: activeLocale,
                defaultLocale: draft.defaultLocale,
              })}
            </span>
          </div>
        )}

        {/* Name */}
        <div className="channel-profile-section__field">
          <label className="channel-profile-section__label" htmlFor="channel-profile-name-input">
            {t('marketplace.channelManage.v3Profile.nameLabel')}
            <span className="channel-profile-section__hint">
              {t('marketplace.channelManage.v3Profile.nameHint')}
            </span>
          </label>
          <input
            id="channel-profile-name-input"
            data-testid="channel-profile-name-input"
            data-field="name"
            type="text"
            className="channel-profile-section__input"
            value={currentName}
            maxLength={NAME_MAX}
            placeholder={isDefault ? '' : (defaultName || '—')}
            onChange={(e) => updateLocaleField('name_i18n', activeLocale, e.target.value.slice(0, NAME_MAX))}
            aria-label={t('marketplace.channelManage.v3Profile.nameLabel')}
          />
        </div>

        {/* Tagline */}
        <div className="channel-profile-section__field">
          <label className="channel-profile-section__label" htmlFor="channel-profile-tagline-input">
            {t('marketplace.channelManage.v3Profile.taglineLabel')}
            <span className="channel-profile-section__hint">
              {t('marketplace.channelManage.v3Profile.taglineHint')}
            </span>
          </label>
          <input
            id="channel-profile-tagline-input"
            data-field="tagline"
            type="text"
            className="channel-profile-section__input"
            value={currentTagline}
            maxLength={TAGLINE_MAX}
            placeholder={isDefault ? '' : (defaultTagline || '—')}
            onChange={(e) => updateLocaleField('tagline_i18n', activeLocale, e.target.value.slice(0, TAGLINE_MAX))}
          />
        </div>

        {/* Description */}
        <div className="channel-profile-section__field">
          <label className="channel-profile-section__label" htmlFor="channel-profile-description-input">
            {t('marketplace.channelManage.v3Profile.descriptionLabel')}
            <span className="channel-profile-section__hint">
              {t('marketplace.channelManage.v3Profile.descriptionHint')}
            </span>
          </label>
          <textarea
            id="channel-profile-description-input"
            data-field="description"
            className="channel-profile-section__textarea"
            value={currentDescription}
            maxLength={DESCRIPTION_MAX}
            rows={4}
            placeholder={isDefault ? '' : (defaultDescription || t('marketplace.channelManage.v3Profile.fallbackPlaceholder'))}
            onChange={(e) => updateLocaleField('description_i18n', activeLocale, e.target.value.slice(0, DESCRIPTION_MAX))}
          />
          <span className="channel-profile-section__char-count">
            {currentDescription.length} / {DESCRIPTION_MAX}
          </span>
        </div>
      </div>

      {/* ── Tags card ────────────────────────────────────────── */}
      <div className="channel-profile-section__card">
        <h2 className="channel-profile-section__card-title">
          {t('marketplace.channelManage.v3Profile.tagsTitle')}
        </h2>
        <p className="channel-profile-section__card-subtitle">
          {t('marketplace.channelManage.v3Profile.tagsSubtitle')}
        </p>
        <div className="channel-profile-section__tags-editor">
          {draft.tags.map((tag) => (
            <ChipAtom
              key={tag}
              label={tag}
              removable
              onRemove={() => handleTagRemove(tag)}
              className="channel-profile-section__tag-chip"
            />
          ))}
          {draft.tags.length < TAGS_MAX && (
            <div className="channel-profile-section__tag-input-row">
              <input
                type="text"
                className="channel-profile-section__input channel-profile-section__input--inline"
                value={tagInput}
                placeholder={t('marketplace.channelManage.v3Profile.tagPlaceholder')}
                onChange={(e) => setTagInput(e.target.value.toLowerCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleTagAdd();
                  }
                }}
                aria-label={t('marketplace.channelManage.v3Profile.tagInputLabel')}
              />
              <button
                type="button"
                className="channel-profile-section__tag-add-btn"
                onClick={handleTagAdd}
                disabled={!tagInput.trim()}
              >
                {t('marketplace.channelManage.v3Profile.tagAddBtn')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Scope & attribution card ─────────────────────────── */}
      <div className="channel-profile-section__card">
        <h2 className="channel-profile-section__card-title">
          {t('marketplace.channelManage.v3Profile.scopeTitle')}
        </h2>
        <p className="channel-profile-section__card-subtitle">
          {t('marketplace.channelManage.v3Profile.scopeSubtitle')}
        </p>

        {/* Scope Cards */}
        <div className="channel-profile-section__scope-cards">
          {(['organization', 'center', 'metasphere'] as const).map((scopeValue) => (
            <button
              key={scopeValue}
              type="button"
              className={`channel-profile-section__scope-card ${draft.scope === scopeValue ? 'channel-profile-section__scope-card--selected' : ''}`}
              onClick={() => update({ scope: scopeValue, publisherSystemName: scopeValue === 'metasphere' ? draft.publisherSystemName : null })}
            >
              <div className="channel-profile-section__scope-card-icon">
                {scopeValue === 'organization' && <i className="pi pi-building" aria-hidden="true" />}
                {scopeValue === 'center' && <i className="pi pi-sitemap" aria-hidden="true" />}
                {scopeValue === 'metasphere' && <i className="pi pi-globe" aria-hidden="true" />}
              </div>
              <div className="channel-profile-section__scope-card-content">
                <h3 className="channel-profile-section__scope-card-title">
                  {t(`marketplace.channelManage.v3Profile.scopeCard.${scopeValue}.title`)}
                </h3>
                <p className="channel-profile-section__scope-card-description">
                  {t(`marketplace.channelManage.v3Profile.scopeCard.${scopeValue}.description`)}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Publisher Dropdown - Only for Marketplace scope */}
        {draft.scope === 'metasphere' && (
          <div className="channel-profile-section__field">
            <label className="channel-profile-section__label">
              {t('marketplace.channelManage.v3Profile.publisherLabel')}
              <span className="channel-profile-section__hint">
                {t('marketplace.channelManage.v3Profile.publisherHint')}
              </span>
            </label>
            <DropdownAtom
              value={draft.publisherSystemName}
              options={publisherOptions}
              onChange={(e) => update({ publisherSystemName: e.value as string | null })}
              optionLabel="label"
              optionValue="value"
              placeholder={t('marketplace.channelManage.v3Profile.publisherPlaceholder')}
              className="channel-profile-section__dropdown"
              showClear
            />
          </div>
        )}
      </div>
    </div>
  );
};

ChannelProfileSectionOrganism.displayName = 'ChannelProfileSectionOrganism';

export default ChannelProfileSectionOrganism;
